import { Random } from '../../java/util/Random';
import { KursblockungDynDaten } from '../../core/kursblockung/KursblockungDynDaten';
import { KursblockungAlgorithmusK, cast_de_svws_nrw_core_kursblockung_KursblockungAlgorithmusK } from '../../core/kursblockung/KursblockungAlgorithmusK';
import { Class } from '../../java/lang/Class';
import { Logger } from '../../core/logger/Logger';
import { System } from '../../java/lang/System';

export class KursblockungAlgorithmusKSchuelervorschlag extends KursblockungAlgorithmusK {

	/**
	 *  Die Anzahl an Runden ohne Verbesserung, bevor es zum Abbruch kommt.
	 */
	private static readonly MAX_RUNDEN_IN_FOLGE_OHNE_VERBESSERUNG: number = 2000;


	/**
	 * Im Konstruktor kann die Klasse die jeweiligen Datenstrukturen aufbauen. Kurse dürfen in diese Methode noch nicht
	 * auf Schienen verteilt werden.
	 *
	 * @param random     Ein {@link Random}-Objekt zur Steuerung des Zufalls über einen Anfangs-Seed.
	 * @param logger     Logger für Benutzerhinweise, Warnungen und Fehler.
	 * @param dynDaten   Die dynamischen Blockungsdaten.
	 */
	public constructor(random: Random, logger: Logger, dynDaten: KursblockungDynDaten) {
		super(random, logger, dynDaten);
	}

	public toString(): string {
		return "Schülervorschlag";
	}

	/**
	 * Der Algorithmus entfernt zunächst alle SuS aus ihren Kursen. Anschließend werden die Kurse zufällig verteilt.
	 * Anschließend verändert der Algorithmus die Lage eines zufälligen Kurses. Falls sich die Bewertung verschlechtert,
	 * wird die Veränderung rückgängig gemacht.
	 */
	public berechne(pEndzeit: number): void {
		const current: number = System.currentTimeMillis();
		const halbzeit: number = current + (Math.trunc((pEndzeit - current) / 2));
		if (this.dynDaten.gibKurseDieFreiSindAnzahl() === 0) {
			return;
		}
		this.dynDaten.aktionSchuelerAusAllenKursenEntfernen();
		this.dynDaten.aktionKurseFreieZufaelligVerteilen();
		this.dynDaten.aktionZustandSpeichernK();
		let countKeineVerbesserung: number = 0;
		do {
			countKeineVerbesserung = this.verteileKurseMitSchuelerwunsch() ? 0 : (countKeineVerbesserung + 1);
		} while ((countKeineVerbesserung < KursblockungAlgorithmusKSchuelervorschlag.MAX_RUNDEN_IN_FOLGE_OHNE_VERBESSERUNG) && (System.currentTimeMillis() < halbzeit));
		countKeineVerbesserung = 0;
		do {
			countKeineVerbesserung = this.verteileKurseZufaelligEinWenig() ? 0 : (countKeineVerbesserung + 1);
		} while ((countKeineVerbesserung < KursblockungAlgorithmusKSchuelervorschlag.MAX_RUNDEN_IN_FOLGE_OHNE_VERBESSERUNG) && (System.currentTimeMillis() < pEndzeit));
	}

	/**
	 * Kurslage wird durch Schüler-Wünsche verändert. Falls sich die Bewertung verschlechtert, wird die Veränderung
	 * rückgängig gemacht.
	 *
	 * @return true, wenn der Zustand angepasst wurde
	 */
	private verteileKurseMitSchuelerwunsch(): boolean {
		this.dynDaten.aktionSchuelerAusAllenKursenEntfernen();
		this.dynDaten.aktionKurseVerteilenNachSchuelerwunsch();
		this.dynDaten.aktionSchuelerVerteilenMitGewichtetenBipartitemMatching();
		const compare: number = this.dynDaten.gibCompareZustandK1NW2KD3FW();
		if (compare >= 0) {
			this.dynDaten.aktionZustandSpeichernK();
			return compare > 0;
		}
		this.dynDaten.aktionZustandLadenK();
		return false;
	}

	/**
	 * Kurslage wird ein wenig zufällig verändert und bewertet. Falls sich die Bewertung verschlechtert, wird die
	 * Veränderung rückgängig gemacht.
	 *
	 * @return true, wenn der Zustand angepasst wurde
	 */
	private verteileKurseZufaelligEinWenig(): boolean {
		do {
			this.dynDaten.aktionSchuelerAusAllenKursenEntfernen();
			this.dynDaten.aktionKursVerteilenEinenZufaelligenFreien();
			this.dynDaten.aktionSchuelerVerteilenMitGewichtetenBipartitemMatching();
			const cmp: number = this.dynDaten.gibCompareZustandK1NW2KD3FW();
			if (cmp > 0) {
				this.dynDaten.aktionZustandSpeichernK();
				return true;
			}
		} while (this.rnd.nextBoolean());
		this.dynDaten.aktionZustandLadenK();
		return false;
	}

	transpilerCanonicalName(): string {
		return 'de.svws_nrw.core.kursblockung.KursblockungAlgorithmusKSchuelervorschlag';
	}

	isTranspiledInstanceOf(name: string): boolean {
		return ['de.svws_nrw.core.kursblockung.KursblockungAlgorithmusKSchuelervorschlag', 'de.svws_nrw.core.kursblockung.KursblockungAlgorithmusK'].includes(name);
	}

	public static readonly class = new Class<KursblockungAlgorithmusKSchuelervorschlag>('de.svws_nrw.core.kursblockung.KursblockungAlgorithmusKSchuelervorschlag');

}

export function cast_de_svws_nrw_core_kursblockung_KursblockungAlgorithmusKSchuelervorschlag(obj: unknown): KursblockungAlgorithmusKSchuelervorschlag {
	return obj as KursblockungAlgorithmusKSchuelervorschlag;
}
